/**
 * Cliente HTTP del detalle de costo de una sesión para `apps/speak`.
 *
 * Pega contra dos endpoints del backend (ambos devuelven shape plano, sin
 * envelope `{ success, data }` — confirmado contra el código):
 *   - `GET /api/sessions/:id/cost`        → desglose por propósito (ledger) + por turno.
 *     (montado en `server.ts`, RLS-scoped al user: 404 si la sesión es de otro)
 *   - `GET /api/me/credits/projection`    → saldo + días restantes al ritmo de gasto.
 *     (`estimatedDaysRemaining: null` si <3 días activos o saldo en cero)
 *
 * Tipos redefinidos localmente (no se importan de `apps/client`) para que
 * speak quede autocontenida — misma decisión que `sessionsApi.ts` /
 * `sessionSummaryApi.ts`. Los montos viajan como string nano-USD (1e8 = $1)
 * para preservar precisión BigInt; el display al usuario es en orbs
 * (`formatOrbs` de `@orborix/shared-types`).
 */

import { createTypedApiClient } from '@orborix/auth-client';
import type { DebitPurpose } from '@orborix/model-catalog';

/** Settlement: 'pending' mientras jobs background (distill/memoria) pueden
 *  seguir debitando; 'settled' cuando ya no se espera más cobro. */
export type SettlementStatus = 'pending' | 'settled';

export interface SessionCostBreakdownItem {
  totalNano: string;
  totalUsd: string;
  calls: number;
}

/**
 * What part of the session's charge paid for one thing (`DebitPurpose`, the
 * ledger's own label). `purpose: null` = a legacy debit nobody could classify.
 */
export interface SessionCostPurpose {
  purpose: DebitPurpose | null;
  totalNano: string;
  totalUsd: string;
  calls: number;
}

export interface SessionCostTurn {
  id: string;
  index: number;
  costNano: string;
  costUsd: string;
  durationMs: number;
  startedAt: string;
}

export interface SessionCost {
  sessionId: string;
  startedAt: string;
  endedAt: string | null;
  settlementStatus: SettlementStatus;
  totalNano: string;
  totalUsd: string;
  totalTokensIn: number;
  totalTokensOut: number;
  totalAudioSeconds: string;
  /** What each orb paid for, straight from the ledger — the sheet's rows. */
  purposes: SessionCostPurpose[];
  /** Per-SERVICE totals (`*_calls` tables). Not a "what for" split: don't build rows from it. */
  breakdown: {
    llm: SessionCostBreakdownItem & { tokensIn: number; tokensOut: number };
    tts: SessionCostBreakdownItem & { characters: number; audioSeconds: string };
    stt: SessionCostBreakdownItem & { audioSeconds: string };
    /** Búsqueda web nativa (cobrada por búsqueda). `searches` = nº de búsquedas. */
    webSearch: SessionCostBreakdownItem & { searches: number };
    /** Modo realtime S2S (hoy sólo coach): un turno = una realtime_call. */
    realtime: SessionCostBreakdownItem & {
      textTokensIn: number;
      textTokensOut: number;
      audioTokensIn: number;
      audioTokensOut: number;
    };
  };
  turns: SessionCostTurn[];
}

export interface CreditsProjection {
  /** Saldo en nano-USD (1e8 = $1). String BigInt-safe. */
  balanceNano: string;
  /** Saldo USD pre-formateado por el backend (2 decimales). */
  balanceUsd: string;
  /** Gasto promedio por día activo (nano-USD), o null sin historial. */
  avgNanoPerDay: string | null;
  /** Gasto promedio por día activo (USD, 4 decimales), o null. */
  avgUsdPerDay: string | null;
  /** Días activos (con gasto) usados para el promedio. */
  activeDaysForAverage: number;
  /**
   * Días estimados restantes al ritmo de gasto. `null` cuando hay <3 días
   * activos o saldo en cero — la UI muestra sólo el saldo.
   */
  estimatedDaysRemaining: number | null;
}

// Rutas absolutas (`/api/sessions/*`, `/api/me/*`) ⇒ cliente sin `basePath`.
// Los endpoints de costo/projection devuelven shape plano; el desenvuelto del
// cliente compartido es defensa en profundidad por si algún día los envuelven
// en `{ success, data }` (mismo criterio que `sessionSummaryApi.ts`).
const client = createTypedApiClient({
  basePath: '',
  errorName: 'CostApiError',
  // El polling del debrief cancela en mid-flight: el caller tiene que poder
  // distinguir "aborté yo" de "se cayó la red".
  abortError: 'rethrow',
});

/** Error con código crudo del backend; el caller decide el copy user-facing. */
export const CostApiError = client.ApiError;
export type CostApiError = InstanceType<typeof client.ApiError>;

function get<T>(url: string, signal?: AbortSignal): Promise<T> {
  return client.get<T>(url, signal ? { signal } : {});
}

/** Desglose de costo de una sesión. `signal` permite cancelar en mid-flight. */
export function fetchSessionCost(sessionId: string, signal?: AbortSignal): Promise<SessionCost> {
  return get<SessionCost>(`/api/sessions/${encodeURIComponent(sessionId)}/cost`, signal);
}

/** Proyección de saldo + días restantes del user al ritmo de gasto. */
export function fetchCreditsProjection(signal?: AbortSignal): Promise<CreditsProjection> {
  return get<CreditsProjection>('/api/me/credits/projection', signal);
}
